import { FieldHelpContent } from "@/components/FieldHelpDialog";
import {
  findReferenceGuideByName,
  getReferenceGuide,
  splitContentIntoSteps,
} from "@/shared/referenceId";

export interface ReferenceHelpConfig {
  companyFields?: string[];
  backendFields?: string[];
  productFields?: string[];
  fallbackLinkLabel?: string;
}

export interface ReferenceHelpResult {
  helpDialog?: FieldHelpContent;
  helpLinkText?: string;
  /** Dynamic checkbox label for "I don't know my {label}" */
  noValueCheckboxLabel?: string;
  /** The label for the reference field (e.g., "Account Number", "Mortgage Number") */
  label?: string;
  /** The subtitle for the reference field (e.g., "Where to find my account number") */
  subtitle?: string;
}

const DEFAULT_COMPANY_FIELDS = [
  "otherCompanyName",
  "referedFromCompany",
  "companyName",
  "company",
  "creditor",
  "supplier",
  "freepostName",
  "companyNameManual",
  "manualCompanyName",
  "companyOtherName",
  "lenderName",
];

const DEFAULT_PRODUCT_FIELDS = ["productName"];

const DEFAULT_BACKEND_FIELDS = [
  "backendName",
  "lenderName",
  "creditor",
  "company",
];

const REFERENCE_FIELD_PATTERN =
  /(reference(number|id)|accountNumber)$/i;

const DEFAULT_LINK_LABEL = "How to find my Reference ID";

const normalizePathSegments = (path: string): string[] => {
  if (!path) {
    return [];
  }
  return path
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .filter((segment) => segment.length > 0);
};

const getValueAtPath = (
  values: Record<string, unknown>,
  path: string
): unknown => {
  const segments = normalizePathSegments(path);
  if (segments.length === 0) {
    return undefined;
  }
  let current: unknown = values;
  for (const segment of segments) {
    if (
      current === null ||
      current === undefined ||
      typeof current !== "object"
    ) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
};

const derivePrefixes = (fieldName: string): string[] => {
  const normalized = fieldName.replace(/\[(\d+)\]/g, ".$1");
  const segments = normalized.split(".");
  const prefixes: string[] = [];
  const pathSegments = [...segments];

  while (pathSegments.length > 0) {
    pathSegments.pop();
    if (pathSegments.length === 0) {
      break;
    }
    prefixes.push(pathSegments.join("."));
  }

  prefixes.push("");
  return prefixes;
};

const buildHelpContent = (guide: ReturnType<typeof getReferenceGuide>): FieldHelpContent => {
  const steps = splitContentIntoSteps(guide.content).map(
    (text, index) => ({
      text,
    })
  );

  const shouldShowImage =
    guide.showImage !== false && typeof guide.image === "string";

  return {
    title: guide.heading,
    instructionsTitle: guide.heading,
    steps:
      steps.length > 0
        ? steps
        : [
            {
              text: guide.content.trim(),
            },
          ],
    showStepNumbers: false,
    image: shouldShowImage ? guide.image : undefined,
  };
};

const findFirstString = (
  values: Record<string, unknown>,
  prefixes: string[],
  fields: string[]
): string | undefined => {
  for (const prefix of prefixes) {
    for (const field of fields) {
      const candidatePath = prefix ? `${prefix}.${field}` : field;
      const candidateValue = getValueAtPath(values, candidatePath);
      if (typeof candidateValue === "string" && candidateValue.trim().length) {
        return candidateValue.trim();
      }
    }
  }
  return undefined;
};

export const resolveReferenceHelp = (
  fieldName: string,
  values: Record<string, unknown>,
  config?: ReferenceHelpConfig
): ReferenceHelpResult => {
  const isReferenceField =
    config ||
    REFERENCE_FIELD_PATTERN.test(fieldName) ||
    fieldName.split(".").some((segment) => REFERENCE_FIELD_PATTERN.test(segment));

  if (!isReferenceField) {
    return {};
  }

  const prefixes = derivePrefixes(fieldName);
  const companyFields = config?.companyFields ?? DEFAULT_COMPANY_FIELDS;
  const productFields = config?.productFields ?? DEFAULT_PRODUCT_FIELDS;
  const backendFields = config?.backendFields ?? DEFAULT_BACKEND_FIELDS;

  // Special rule (requested): if a product is selected, try to match a guide for that product first.
  // If no product-specific guide exists, fall back to the normal company/backend lookup.
  const productName = findFirstString(values, prefixes, productFields);
  const companyName = findFirstString(values, prefixes, companyFields);
  const backendName = findFirstString(values, prefixes, backendFields);

  const guide =
    findReferenceGuideByName(productName) ??
    getReferenceGuide({ companyName, backendName });

  return {
    helpDialog: buildHelpContent(guide),
    helpLinkText: guide.linkLabel || config?.fallbackLinkLabel || DEFAULT_LINK_LABEL,
    noValueCheckboxLabel: guide.noValueCheckboxLabel,
    label: guide.label,
    subtitle: guide.subtitle,
  };
};

export const DEFAULT_REFERENCE_HELP_LINK = DEFAULT_LINK_LABEL;
